import { useCallback, useState } from 'react';

export interface UseUndoOptions {
  /** Update current state without pushing previous to past (e.g. crop drag) */
  skipHistory?: boolean;
  /** Replace current without pushing to past and clear future (e.g. init, revert) */
  replace?: boolean;
}

/**
 * Undo/redo state with past and future stacks.
 * setState(newState) pushes current to past and sets current to newState.
 * undo/redo move between past and future without creating new history entries.
 */
export function useUndo<T>(initialState: T) {
  const [state, setStateInternal] = useState<{
    current: T;
    past: T[];
    future: T[];
  }>({
    current: initialState,
    past: [],
    future: [],
  });

  const setState = useCallback((value: T | ((prev: T) => T), options?: UseUndoOptions) => {
    const { skipHistory = false, replace = false } = options ?? {};

    setStateInternal((prev) => {
      const next = typeof value === 'function' ? (value as (p: T) => T)(prev.current) : value;

      if (replace) {
        return { current: next, past: [], future: [] };
      }
      if (skipHistory) {
        return { ...prev, current: next };
      }
      return {
        current: next,
        past: [...prev.past, prev.current],
        future: [],
      };
    });
  }, []);

  const undo = useCallback(() => {
    setStateInternal((prev) => {
      if (prev.past.length === 0) return prev;
      const previous = prev.past[prev.past.length - 1];
      return {
        current: previous,
        past: prev.past.slice(0, -1),
        future: [prev.current, ...prev.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    setStateInternal((prev) => {
      if (prev.future.length === 0) return prev;
      const next = prev.future[0];
      return {
        current: next,
        past: [...prev.past, prev.current],
        future: prev.future.slice(1),
      };
    });
  }, []);

  return {
    state: state.current,
    setState,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
}
